import { useState, useEffect, useCallback, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { useApi } from '@/hooks/useApi'
import { useAuth } from '@/hooks/useAuth'
import { useRealtimeEvent } from '@/hooks/useRealtimeEvents'
import { showToast } from '@/components/layout/Toast'
import type { AsistenteSesion, AsistenteMensaje } from '@/lib/types'

/**
 * Asistente de Contabilidad — burbuja flotante (admin).
 * Fase 1: chat + historial. Fase 2: subir comprobantes → la IA extrae y propone
 * egresos que se confirman (y registran en Flujo de Caja).
 */
export default function AsistenteBubble() {
  const { user } = useAuth()
  // Métodos estables (el objeto de useApi() no lo es → evitar loop de render).
  const { get, post, put, upload } = useApi()

  const [open, setOpen] = useState(false)
  const [view, setView] = useState<'list' | 'chat'>('list')
  const [sesiones, setSesiones] = useState<AsistenteSesion[]>([])
  const [activeId, setActiveId] = useState<number | null>(null)
  const [mensajes, setMensajes] = useState<AsistenteMensaje[]>([])
  const [input, setInput] = useState('')
  const [sending, setSending] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [confirmadas, setConfirmadas] = useState<Set<string>>(new Set())
  const [hayNoLeidos, setHayNoLeidos] = useState(false)
  const [pendientes, setPendientes] = useState<File[]>([])
  const fileRef = useRef<HTMLInputElement>(null)
  const endRef = useRef<HTMLDivElement>(null)

  const esAdmin = user?.role === 'admin'
  // Ocultar la burbuja en el módulo de Conversaciones (choca con el chat omnicanal).
  const { pathname } = useLocation()
  const enConversaciones = pathname === '/conversaciones' || pathname.startsWith('/conversaciones/')

  const loadSesiones = useCallback(async () => {
    try { setSesiones(await get<AsistenteSesion[]>('/api/asistente/sesiones')) } catch { /* silencioso */ }
  }, [get])

  const fetchNoLeidos = useCallback(async () => {
    try {
      const r = await get<{ total: number }>('/api/asistente/no-leidos')
      setHayNoLeidos((r?.total || 0) > 0)
    } catch { /* silencioso */ }
  }, [get])

  useEffect(() => { if (open) loadSesiones() }, [open, loadSesiones])
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }) }, [mensajes])
  // Seed del punto verde al montar (respuestas pendientes de sesiones anteriores).
  useEffect(() => { if (esAdmin) fetchNoLeidos() }, [esAdmin, fetchNoLeidos])

  const refetchMensajes = useCallback(async (id: number): Promise<AsistenteMensaje[]> => {
    const r = await get<{ sesion: AsistenteSesion; mensajes: AsistenteMensaje[] }>(`/api/asistente/sesiones/${id}`)
    setMensajes(r.mensajes)
    return r.mensajes
  }, [get])

  // Fase 4: el backend avisa por SSE cuando termina de procesar un job (aunque el panel
  // esté cerrado). Si justo estás viendo esa sesión, refresca en vivo; si no, prende el punto.
  useRealtimeEvent('asistente_respuesta', (data: { sesion_id?: number }) => {
    const sid = data?.sesion_id
    if (open && view === 'chat' && activeId && sid === activeId) {
      refetchMensajes(activeId)
        .then(() => { put(`/api/asistente/sesiones/${activeId}/leido`, {}).catch(() => {}) })
        .catch(() => {})
      setUploading(false)
    } else {
      setHayNoLeidos(true)
      if (open) loadSesiones()
    }
  })

  async function abrirSesion(id: number) {
    try {
      setPendientes([]); setInput('')
      await refetchMensajes(id)
      setActiveId(id); setView('chat')
      await put(`/api/asistente/sesiones/${id}/leido`, {}).catch(() => {})
      fetchNoLeidos()
    } catch { /* silencioso */ }
  }

  async function nuevaSesion() {
    try {
      const ses = await post<AsistenteSesion>('/api/asistente/sesiones', {})
      setPendientes([]); setInput('')
      setSesiones(s => [ses, ...s]); setActiveId(ses.id); setMensajes([]); setView('chat')
    } catch { /* silencioso */ }
  }

  async function enviar() {
    const texto = input.trim()
    if (!texto || !activeId || sending) return
    setSending(true); setInput('')
    try {
      const msg = await post<AsistenteMensaje>(`/api/asistente/sesiones/${activeId}/mensaje`, { contenido: texto })
      setMensajes(m => [...m, msg])
      loadSesiones()
    } catch { setInput(texto) } finally { setSending(false) }
  }

  // Enviar: si hay archivos adjuntados, los sube junto con el texto como contexto para la IA;
  // si no, manda el texto como mensaje suelto.
  async function handleSend() {
    if (uploading || sending) return
    if (pendientes.length) {
      const files = pendientes; const contexto = input.trim()
      setPendientes([]); setInput('')
      await subirArchivos(files, contexto)
    } else if (input.trim()) {
      await enviar()
    }
  }

  async function subirArchivos(files: File[], contexto = '') {
    const id = activeId
    if (!id || !files.length || uploading) return
    setUploading(true)
    try {
      const fd = new FormData()
      files.forEach(f => fd.append('archivos', f))
      if (contexto) fd.append('mensaje', contexto)
      await upload(`/api/asistente/sesiones/${id}/upload`, fd)
      await refetchMensajes(id)
      // El procesamiento es async: sondeamos hasta que llegue la respuesta del asistente.
      let intentos = 0
      const antes = (await refetchMensajes(id)).length
      const iv = setInterval(async () => {
        intentos++
        try {
          const nuevos = await refetchMensajes(id)
          const llego = nuevos.length > antes && nuevos[nuevos.length - 1].rol === 'assistant'
          if (llego || intentos >= 20) {
            clearInterval(iv); setUploading(false)
            await put(`/api/asistente/sesiones/${id}/leido`, {}).catch(() => {})
            fetchNoLeidos()
          }
        } catch { /* reintenta */ }
      }, 3000)
    } catch {
      showToast('Error al subir los comprobantes', 'error'); setUploading(false)
    }
  }

  async function confirmarPropuesta(key: string, prop: Record<string, unknown>, opciones: Record<string, unknown>) {
    const [mid, idx] = key.split('-').map(Number)
    try {
      await post('/api/asistente/propuestas/confirmar', { propuesta: prop, opciones, mensaje_id: mid, idx })
      setConfirmadas(s => { const n = new Set(s); n.add(key); return n })
      showToast('Egreso registrado en Flujo de Caja', 'success')
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Error al registrar el egreso', 'error')
    }
  }

  function fmt(iso: string): string {
    try { return new Date(iso).toLocaleString('es-PE', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) }
    catch { return '' }
  }

  if (!esAdmin || enConversaciones) return null

  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)} aria-label="Abrir asistente" title="Asistente de Contabilidad"
          style={{
            position: 'fixed', bottom: 24, right: 24, zIndex: 900, width: 56, height: 56, borderRadius: '50%',
            border: 'none', cursor: 'pointer', background: 'var(--accent-primary)', color: 'var(--text-on-primary)',
            boxShadow: '0 6px 20px rgba(var(--shadow-rgb), calc(.25 * var(--shadow-k)))', display: 'grid', placeItems: 'center',
          }}>
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /><path d="M8.5 10.5l1.2 1.2 2.3-2.6" /><circle cx="16" cy="10" r="1" />
          </svg>
          {hayNoLeidos && (
            <span aria-label="Respuestas sin leer" style={{
              position: 'absolute', top: 8, right: 8, width: 14, height: 14, borderRadius: '50%',
              background: 'var(--ok, #2ecc71)', border: '2px solid var(--bg-card, #1a1a2e)',
            }} />
          )}
        </button>
      )}

      {open && (
        <div style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 901,
          width: 'min(400px, calc(100vw - 32px))', height: 'min(600px, calc(100vh - 48px))',
          background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 16,
          boxShadow: '0 12px 40px rgba(var(--shadow-rgb), calc(.35 * var(--shadow-k)))', display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', borderBottom: '1px solid var(--border)', background: 'var(--bg-secondary)' }}>
            {view === 'chat' && (
              <button onClick={() => { setView('list'); loadSesiones() }} title="Historial"
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: 4, display: 'grid', placeItems: 'center' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 18l-6-6 6-6" /></svg>
              </button>
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: '.95rem' }}>Asistente</div>
              <div style={{ fontSize: '.72rem', color: 'var(--text-secondary)' }}>Contabilidad</div>
            </div>
            <button onClick={nuevaSesion} title="Nueva conversación"
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent-primary)', padding: 4, display: 'grid', placeItems: 'center' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14" /></svg>
            </button>
            <button onClick={() => setOpen(false)} title="Cerrar"
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: 4, display: 'grid', placeItems: 'center' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg>
            </button>
          </div>

          {view === 'list' && (
            <div style={{ flex: 1, overflowY: 'auto', padding: 8 }}>
              <button onClick={nuevaSesion} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', marginBottom: 8, background: 'var(--accent-primary)', color: 'var(--text-on-primary)', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 600, fontSize: '.88rem' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14" /></svg>
                Nueva conversación
              </button>
              {sesiones.length === 0 ? (
                <div style={{ textAlign: 'center', color: 'var(--text-secondary)', fontSize: '.85rem', padding: '2rem 1rem' }}>Sin conversaciones todavía.<br />Creá una para empezar.</div>
              ) : sesiones.map(s => (
                <button key={s.id} onClick={() => abrirSesion(s.id)} style={{ width: '100%', textAlign: 'left', padding: '10px 12px', marginBottom: 4, background: 'transparent', border: '1px solid var(--border)', borderRadius: 10, cursor: 'pointer' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                    {(s.no_leidos ?? 0) > 0 && <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--ok, #2ecc71)', flexShrink: 0 }} />}
                    <div style={{ flex: 1, minWidth: 0, fontWeight: 600, fontSize: '.86rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.titulo || 'Nueva conversación'}</div>
                  </div>
                  <div style={{ fontSize: '.72rem', color: 'var(--text-secondary)', marginTop: 2 }}>{fmt(s.updated_at)}</div>
                </button>
              ))}
            </div>
          )}

          {view === 'chat' && (
            <>
              <div style={{ flex: 1, overflowY: 'auto', padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ fontSize: '.78rem', color: 'var(--text-secondary)', background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 10px', textAlign: 'center' }}>
                  Adjuntá comprobantes de gasto (imágenes o PDF) con el clip 📎 — podés escribir una nota de contexto antes de enviarlos — y te propongo el egreso para registrarlo en Flujo de Caja.
                </div>
                {mensajes.map(m => (
                  <div key={m.id} style={{ display: 'flex', flexDirection: 'column', alignItems: m.rol === 'user' ? 'flex-end' : 'flex-start', gap: 6 }}>
                    {m.contenido && (
                      <div style={{ maxWidth: '85%', padding: '8px 12px', borderRadius: 12, fontSize: '.88rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word', background: m.rol === 'user' ? 'var(--accent-primary)' : 'var(--bg-secondary)', color: m.rol === 'user' ? 'var(--text-on-primary)' : 'inherit', border: m.rol === 'user' ? 'none' : '1px solid var(--border)' }}>
                        {m.contenido}
                      </div>
                    )}
                    {Array.isArray(m.propuestas) && (m.propuestas as Record<string, unknown>[]).map((p, i) => (
                      <PropuestaCard key={i} prop={p} confirmada={confirmadas.has(`${m.id}-${i}`) || p.estado === 'confirmado'}
                        onConfirm={(edit, opciones) => confirmarPropuesta(`${m.id}-${i}`, edit, opciones)} />
                    ))}
                  </div>
                ))}
                {uploading && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-secondary)', fontSize: '.82rem', padding: '4px 2px' }}>
                    <div className="auth-spinner" style={{ width: 14, height: 14 }} /> Leyendo los comprobantes…
                  </div>
                )}
                <div ref={endRef} />
              </div>
              <div style={{ borderTop: '1px solid var(--border)' }}>
                {pendientes.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '8px 10px 0' }}>
                    {pendientes.map((f, i) => (
                      <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, maxWidth: '100%', background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 8, padding: '4px 8px', fontSize: '.72rem' }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0, opacity: .7 }}><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" /></svg>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 170 }}>{f.name}</span>
                        <button onClick={() => setPendientes(p => p.filter((_, j) => j !== i))} disabled={uploading} title="Quitar"
                          style={{ background: 'none', border: 'none', cursor: uploading ? 'default' : 'pointer', color: 'var(--text-secondary)', padding: 0, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg>
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8, padding: 10, alignItems: 'center' }}>
                  <input ref={fileRef} type="file" accept="image/*,application/pdf" multiple style={{ display: 'none' }}
                    onChange={e => { const fs = Array.from(e.target.files || []); if (fs.length) setPendientes(p => [...p, ...fs]); e.target.value = '' }} />
                  <button onClick={() => fileRef.current?.click()} disabled={uploading} title="Adjuntar comprobantes"
                    style={{ width: 40, height: 40, flexShrink: 0, borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-secondary)', cursor: uploading ? 'default' : 'pointer', display: 'grid', placeItems: 'center' }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" /></svg>
                  </button>
                  <input value={input} onChange={e => setInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend() } }}
                    placeholder={pendientes.length ? 'Agregá contexto (opcional)…' : 'Escribí un mensaje…'}
                    style={{ flex: 1, minWidth: 0, boxSizing: 'border-box', padding: '10px 12px', borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'inherit', fontSize: '.88rem', outline: 'none' }} />
                  <button onClick={handleSend} disabled={uploading || (!input.trim() && !pendientes.length)} title={pendientes.length ? 'Enviar comprobantes' : 'Enviar'}
                    style={{ width: 40, height: 40, flexShrink: 0, borderRadius: 10, border: 'none', cursor: (uploading || (!input.trim() && !pendientes.length)) ? 'default' : 'pointer', background: 'var(--accent-primary)', color: 'var(--text-on-primary)', opacity: (uploading || (!input.trim() && !pendientes.length)) ? .5 : 1, display: 'grid', placeItems: 'center' }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" /></svg>
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </>
  )
}

// ─── Tarjeta de egreso propuesto (editable antes de confirmar) ───
function PropuestaCard({ prop, confirmada, onConfirm }: {
  prop: Record<string, any>
  confirmada: boolean
  onConfirm: (edit: Record<string, unknown>, opciones: Record<string, unknown>) => void
}) {
  const [e, setE] = useState({
    proveedor: prop.proveedor ?? '',
    descripcion: prop.descripcion ?? '',
    monto: prop.monto ?? '',
    moneda: prop.moneda ?? 'PEN',
    fecha: prop.fecha ?? '',
    categoria: prop.categoria ?? prop.categoria_sugerida ?? '',
  })
  const [opts, setOpts] = useState({
    agregarProveedor: !!prop.proveedor_nuevo,
    crearCategoria: !!prop.categoria_nueva,
    crearRecurrente: !!prop.es_membresia,
    periodicidad: prop.periodicidad === 'anual' ? 'anual' : 'mensual',
  })

  if (prop.error) {
    return <div style={{ maxWidth: '85%', fontSize: '.82rem', color: 'var(--danger, #e94560)', border: '1px solid var(--border)', borderRadius: 10, padding: '8px 10px' }}>
      No pude leer {prop.archivo?.nombre || 'un archivo'}.
    </div>
  }

  const fld = (label: string, val: string, on: (v: string) => void, type = 'text') => (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: '.68rem', color: 'var(--text-secondary)', minWidth: 0 }}>
      {label}
      <input type={type} value={val} disabled={confirmada} onChange={ev => on(ev.target.value)}
        style={{ width: '100%', minWidth: 0, boxSizing: 'border-box', padding: '5px 7px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'inherit', fontSize: '.82rem' }} />
    </label>
  )

  return (
    <div style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box', border: '1px solid var(--border)', borderRadius: 12, padding: 10, background: 'var(--bg-secondary)', display: 'flex', flexDirection: 'column', gap: 8, opacity: confirmada ? .7 : 1 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: '.72rem', fontWeight: 700, color: 'var(--accent-primary)' }}>Egreso propuesto{Array.isArray(prop.archivos) && prop.archivos.length > 1 ? ` · ${prop.archivos.length} documentos` : ''}</span>
        {typeof prop.confianza === 'number' && <span style={{ fontSize: '.66rem', color: 'var(--text-secondary)' }}>confianza {Math.round(prop.confianza * 100)}%</span>}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
        {fld('Proveedor', e.proveedor, v => setE({ ...e, proveedor: v }))}
        {fld('Categoría', e.categoria, v => setE({ ...e, categoria: v }))}
        <label style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: '.68rem', color: 'var(--text-secondary)', minWidth: 0 }}>Monto
          <div style={{ display: 'flex', gap: 4, minWidth: 0 }}>
            <input type="number" value={e.monto} disabled={confirmada} onChange={ev => setE({ ...e, monto: ev.target.value })}
              style={{ flex: 1, minWidth: 0, boxSizing: 'border-box', padding: '5px 7px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'inherit', fontSize: '.82rem' }} />
            <select value={e.moneda} disabled={confirmada} onChange={ev => setE({ ...e, moneda: ev.target.value })}
              style={{ padding: '5px 4px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'inherit', fontSize: '.78rem' }}>
              <option>PEN</option><option>USD</option><option>EUR</option>
            </select>
          </div>
        </label>
        {fld('Fecha', e.fecha, v => setE({ ...e, fecha: v }), 'date')}
      </div>
      {prop.categoria_de_proveedor && (
        <div style={{ fontSize: '.66rem', color: 'var(--accent-primary)', display: 'flex', alignItems: 'center', gap: 4, marginTop: -3 }}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M20 6L9 17l-5-5" /></svg>
          Usé la categoría habitual de {e.proveedor || 'este proveedor'}
        </div>
      )}
      {fld('Descripción', e.descripcion, v => setE({ ...e, descripcion: v }))}
      {!confirmada && (prop.proveedor_nuevo || prop.categoria_nueva || prop.es_membresia) && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: '.74rem', color: 'var(--text-secondary)', borderTop: '1px dashed var(--border)', paddingTop: 6 }}>
          {prop.proveedor_nuevo && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <input type="checkbox" checked={opts.agregarProveedor} onChange={ev => setOpts({ ...opts, agregarProveedor: ev.target.checked })} />
              Agregar “{e.proveedor}” a proveedores
            </label>
          )}
          {prop.categoria_nueva && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <input type="checkbox" checked={opts.crearCategoria} onChange={ev => setOpts({ ...opts, crearCategoria: ev.target.checked })} />
              Crear categoría “{e.categoria}”
            </label>
          )}
          {prop.es_membresia && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', flexWrap: 'wrap' }}>
              <input type="checkbox" checked={opts.crearRecurrente} onChange={ev => setOpts({ ...opts, crearRecurrente: ev.target.checked })} />
              Registrar como pago recurrente
              <select value={opts.periodicidad} disabled={!opts.crearRecurrente} onChange={ev => setOpts({ ...opts, periodicidad: ev.target.value })}
                style={{ padding: '2px 4px', borderRadius: 4, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'inherit', fontSize: '.72rem' }}>
                <option value="mensual">mensual</option><option value="anual">anual</option>
              </select>
            </label>
          )}
        </div>
      )}
      {!confirmada ? (
        <button onClick={() => onConfirm({ ...prop, ...e, monto: Number(e.monto) }, opts)}
          disabled={!e.monto || !e.moneda}
          style={{ padding: '8px', borderRadius: 8, border: 'none', background: 'var(--accent-primary)', color: 'var(--text-on-primary)', cursor: 'pointer', fontWeight: 600, fontSize: '.82rem', opacity: !e.monto || !e.moneda ? .5 : 1 }}>
          Confirmar egreso
        </button>
      ) : (
        <div style={{ textAlign: 'center', fontSize: '.8rem', color: 'var(--ok, #2ecc71)', fontWeight: 600 }}>✓ Registrado en Flujo de Caja</div>
      )}
    </div>
  )
}
